import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../core/button';
import { Label } from '../../../core/label';
import { TextArea } from '../../../core/textarea';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { useDraft } from '../../hooks/use-draft';

interface WrittenExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

export function WrittenExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: WrittenExerciseProps) {
  const initialAnswer = useMemo(() => {
    const submission = content.lastSubmission;
    if (
      submission?.type === ExerciseType.LongAnswer ||
      submission?.type === ExerciseType.ShortAnswer
    ) {
      return submission.answer ?? '';
    }
    return '';
  }, [content.lastSubmission]);

  const { value: answer, setValue: setAnswer, clear: clearDraft } = useDraft<string>(
    ['written', content.exerciseId, content.id],
    initialAnswer,
    { prefix: 'p2e-exercise-draft' },
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type:
          content.exerciseSubtype === ExerciseType.LongAnswer
            ? ExerciseType.LongAnswer
            : ExerciseType.ShortAnswer,
        answer,
      });
      clearDraft();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-4',
        variant === 'card' && 'rounded-lg border border-border bg-card p-4 shadow-sm'
      )}
    >
      {content.title && (
        <h3 className="text-lg font-semibold">
          {renderInlineMarkdown(content.title, 'written-exercise-title')}
        </h3>
      )}
      <ContentDescription text={content.description} markdown />
      <div className="space-y-2">
        <Label htmlFor={`${content.id}-answer`}>Your answer</Label>
        <TextArea
          id={`${content.id}-answer`}
          value={answer}
          onChange={(event) => {
            if (isReadOnly) {
              return;
            }
            setAnswer(event.target.value);
            setLastSavedAt(Date.now());
          }}
          placeholder="Share your thoughts..."
          className="min-h-[120px]"
          readOnly={isReadOnly}
        />
        {!isReadOnly && lastSavedAt ? (
          <p className="text-xs text-muted-foreground">
            Draft saved
          </p>
        ) : null}
      </div>
      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />
      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={!answer.trim() || isSubmitting}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto"
          >
            Submit answer
          </Button>
        </div>
      )}
    </form>
  );
}
